import React from 'react';
import { TripTypes } from '@model/common';
import { VideoGuide } from '@model/content';
import { Page } from '@model/common';
import styled from '@emotion/styled';
import { Theme, ThemeProps } from '@theme/base';
import { Grid } from '@mui/material';
import { GuideLink, GuidesLink } from '@components/common/next-link';
import { withTheme } from '@emotion/react';
import { CtaButton, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { useI18NextContext } from '@components/hooks';
import { SimpleTile } from '@components/common/simple-tile';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';

/* ***************** *
 *       Types       *
 * ***************** */
export interface VideoGuidesComponentProps {
  theme: Theme;
  destination?: string;
  path: string;
  tripType?: TripTypes;
  guides: Array<VideoGuide>;
}

/* ****************** *
 *       Styles       *
 * ****************** */
const MoreVideosGridContainer: any = styled(Grid)(({ theme }: ThemeProps) => ({
  ['&.MuiGrid-spacing-xs-4']: {
    marginTop: theme.custom.spacing.large
  }
}));

/* ************************** *
 *    VideoGuidesComponent    *
 * ************************** */

export const VideoGuidesComponent = ({ destination, path, tripType, guides, theme }: VideoGuidesComponentProps) => {
  if (!destination || !guides.length) {
    return null;
  }

  const t: any = useI18NextContext();

  const MAX_NUMBER_OF_TILES = 3;

  const renderMoreVideoGuides = () =>
    guides.map((videoGuide: VideoGuide, index: number) => {
      const { title, shortDescription, hero, targetPageConfigPath } = videoGuide;
      return (
        index < MAX_NUMBER_OF_TILES && (
          <Grid item xs={12} sm={6} lg={4} key={targetPageConfigPath}>
            <GuideLink path={path} guidePath={targetPageConfigPath} tripType={tripType} guidePage={Page.VIDEO_GUIDES}>
              <SimpleTile title={title} shortDescription={shortDescription} imageUrl={hero.url} />
            </GuideLink>
          </Grid>
        )
      );
    });

  const moreVideos = renderMoreVideoGuides();

  return (
    <>
      <SectionTitle heading={t('video-guides__page-title--label', { destination })} size={SectionTitleSizes.LARGE} />
      <MoreVideosGridContainer container spacing={theme.custom.gridSpacing.default}>
        {moreVideos}
      </MoreVideosGridContainer>
      {moreVideos.length > MAX_NUMBER_OF_TILES && (
        <GuidesLink path={path} tripType={tripType} guidePage={Page.VIDEO_GUIDES}>
          <CtaButton
            label={t('video-guides__label--view-more-videos')}
            color={ButtonColor.SECONDARY}
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.REGULAR}
          />
        </GuidesLink>
      )}
    </>
  );
};

export const VideoGuides = withTheme(VideoGuidesComponent);
